/**
 * Tags API Client with React Query Hooks
 *
 * Provides mutation hooks for tag management.
 * Tags are loaded from initial data, so no fetch hooks needed.
 *
 * Note: With Story 15.7.2, tags are seeded via migration with localizations.
 * Admin can update tag localizations and delete unused tags.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiPut } from 'services/api';
import logger from 'utils/logger';

// ============================================================================
// API Functions
// ============================================================================

/**
 * Update a tag's localized name for a specific language
 * Story 15.8: Tag IDs are now integers
 */
const updateTagLocalization = async (
  tagId: number,
  languageCode: string,
  localizedName: string
): Promise<{ tag_id: number; language_code: string; localized_name: string }> => {
  return apiPut(`/api/admin/tags/${tagId}/localizations/${languageCode}`, {
    localized_name: localizedName,
  });
};

/**
 * Delete a tag (only if not used by any episodes)
 * Story 15.8: Tag IDs are now integers
 */
const deleteTag = async (tagId: number): Promise<void> => {
  await apiDelete<void>(`/api/admin/tags/${tagId}`);
};

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Update a tag's localized name
 */
export const useUpdateTagLocalization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tagId,
      languageCode,
      localizedName,
    }: {
      tagId: number;
      languageCode: string;
      localizedName: string;
    }) => updateTagLocalization(tagId, languageCode, localizedName),
    onSuccess: () => {
      // Invalidate initial data to reload tags with updated localization
      queryClient.invalidateQueries({ queryKey: ['initialData'] });
    },
    onError: (error) => {
      logger.error('Error updating tag localization:', error);
    },
  });
};

/**
 * Delete a tag
 */
export const useDeleteTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tagId: number) => deleteTag(tagId),
    onSuccess: () => {
      // Invalidate initial data to reload tags
      queryClient.invalidateQueries({ queryKey: ['initialData'] });
    },
    onError: (error) => {
      logger.error('Error deleting tag:', error);
    },
  });
};
